import React from "react";

import { cn } from "../../../common/u1/cn";
import { Button } from "../../../common/u1/Button";
import { Input } from "../../../common/u1/Input";
import { SelectField } from "../../../common/u1/SelectField";
import { CountrySelect } from "../../../common/u1/CountrySelect";
import { PhoneCountrySelect } from "../../../common/u1/PhoneCountrySelect";
import { FieldLabel } from "../../../common/u1/FieldLabel";
import { CloseIcon } from "../../../../assets/ui/icons";
import PhoneNumberInput from "../../../common/PhoneNumberInput";
import { WorkingHoursFields } from "./WorkingHoursFields";
import { useAddressForm } from "./useAddressForm";

/* Add / edit one address, as the design's dialog. */
export const AddressFormModal = ({
  address,
  primaryAddress,
  onClose,
  refetch,
  className = "",
}) => {
  const {
    values,
    setField,
    errors,
    states,
    countryLocked,
    onCountryChange,
    isSaving,
    submit,
  } = useAddressForm({ address, primaryAddress, onDone: onClose, refetch });

  /* Transcribed title distinction: an existing record is an edit, otherwise it
     is new. The wording is the design's. */
  const editing = Boolean(address);

  return (
    <div
      className={cn(
        "u1-root flex max-h-[85vh] w-[min(92vw,560px)] flex-col overflow-hidden",
        "rounded-[var(--radius-modal)] shadow-[shadow:var(--shadow-lg)] bg-white text-left",
        className
      )}
    >
      <header className="flex shrink-0 items-start justify-between gap-4 px-5 pb-1 pt-5 sm:px-6">
        <h2 className="m-0 text-[17px] font-bold text-[var(--ink)] font-sans">
          {editing ? "Edit Address & Contact" : "Add Address & Contact"}
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className={cn(
            "flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
            "bg-[var(--slate-2)] text-[var(--muted)] transition-colors",
            "hover:bg-[var(--line)] focus-visible:outline-none",
            "focus-visible:ring-2 focus-visible:ring-[var(--coral)]"
          )}
        >
          <CloseIcon size={14} />
        </button>
      </header>

      {/* The form scrolls; the Save button below it does not. */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        className="flex min-h-0 flex-1 flex-col"
      >
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 sm:px-6">
          <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
            <Input
              label="Address Line 1"
              id="u1-address-1"
              value={values.address_line_1}
              onChange={(e) => setField("address_line_1", e.target.value)}
              error={errors.address_line_1}
              placeholder="Suite/Building/Floor No."
            />
            <Input
              label="Address Line 2"
              id="u1-address-2"
              value={values.address_line_2}
              onChange={(e) => setField("address_line_2", e.target.value)}
              placeholder="Enter Street/Locality"
            />

            <Input
              label="Area"
              id="u1-area"
              value={values.area}
              onChange={(e) => setField("area", e.target.value)}
              placeholder="Enter Area"
            />
            <Input
              label="Postal Code"
              id="u1-postal"
              value={values.postal_code}
              onChange={(e) => setField("postal_code", e.target.value)}
              placeholder="Enter Postal Code"
            />

            {/* The shared searchable picker rather than a 242-row native
                select — the same menu every country field uses. */}
            <CountrySelect
              label="Country"
              labelVariant="field"
              id="u1-country"
              value={values.country}
              onChange={(c) => onCountryChange(c?.code)}
              /* Transcribed restriction: an existing record's country cannot be
                 changed. Disabled as a whole rather than option by option. */
              disabled={countryLocked}
              error={errors.country}
              placeholder="Select a country"
            />

            {states?.length > 0 ? (
              <SelectField
                label="State"
                labelVariant="field"
                id="u1-state"
                value={values.state}
                onChange={(e) => setField("state", e.target.value)}
                error={errors.state}
                placeholder="Select a state"
                options={states.map((s) => ({ value: s.name, label: s.name }))}
              />
            ) : (
              <Input
                label="State"
                id="u1-state"
                value={values.state}
                onChange={(e) => setField("state", e.target.value)}
                error={errors.state}
                placeholder="Enter a state"
                /* Transcribed: no country chosen, nothing to state. */
                disabled={!values.country}
              />
            )}

            <Input
              label="City"
              id="u1-city"
              value={values.city}
              onChange={(e) => setField("city", e.target.value)}
              error={errors.city}
              placeholder="Enter a City"
            />
            <Input
              label="Email Address"
              id="u1-email"
              type="email"
              value={values.email}
              onChange={(e) => setField("email", e.target.value)}
              error={errors.email}
              placeholder="Email Address"
            />
          </div>

          <div className="mt-3.5">
            {/* PhoneNumberInput renders its own control, so the label is a
                span rather than a <label> pointing at nothing. */}
            <FieldLabel as="span" variant="field">
              Phone
            </FieldLabel>
            <PhoneNumberInput
              id="u1-phone"
              phone={values.telephone}
              setPhone={(next) => setField("telephone", next)}
              border
              verifyPhoneNumber={false}
              countrySelect={PhoneCountrySelect}
              /* UAE, not the viewer's geo-detected country: this is a UAE
                 business directory and the number being entered is the
                 company's, not the person filling the form in. */
              defaultCountry="AE"
            />
            {/* Rendered here rather than passed to the control: PhoneNumberInput takes an `error` prop and never renders it, so the live form's "Phone number is required."... */}
            {errors.telephone && (
              <p
                role="alert"
                className="mt-1.5 text-[12px] text-[var(--coral-deep)] font-sans"
              >
                {errors.telephone}
              </p>
            )}
          </div>

          <WorkingHoursFields
            className="mt-5"
            value={values.working_hours}
            onChange={(next) => setField("working_hours", next)}
            error={errors.working_hours}
          />
        </div>

        <div className="shrink-0 border-t border-[var(--line)] px-5 py-4 sm:px-6">
          <Button
            type="submit"
            variant="coralSolid"
            disabled={isSaving}
            className="w-full"
          >
            {isSaving ? "Saving…" : "Save"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AddressFormModal;
